import { useMemo } from 'react';
import { graphql } from 'react-relay';
import { createAri } from '@atlassian/jira-platform-ari';
import { useFetchQuery } from '@atlassian/jira-relay-utils/src/services/use-fetch-query/index.tsx';
import type { getVersions_jira_deployments_versionsForProject_Query$data as QueryResponse } from '@atlassian/jira-relay/src/__generated__/getVersions_jira_deployments_versionsForProject_Query.graphql';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ServiceQueryResult } from '@atlassian/jira-software-deployments-service-query-types/src/services/types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

export type Version = {
	id: string;
	name: string;
};

const transformQueryResult = ({
	loading,
	error,
	data,
}: {
	loading: boolean;
	error: Error | null;
	data: QueryResponse | null;
}): ServiceQueryResult<Version[]> => {
	if (loading) {
		return {
			loading: true,
		};
	}
	if (error) {
		return {
			error,
			loading: false,
		};
	}
	if (!data) {
		return {
			loading: false,
			error: Error('Versions not found'),
		};
	}
	const transformationResult = data.jira.versionsForProject.edges
		.filter(Boolean)
		.map((edge) => edge.node)
		.map(({ name }) => ({ id: name, name }));

	return {
		data: transformationResult,
		loading: false,
	};
};

export const useFixVersions = (projectId: ProjectId): ServiceQueryResult<Version[]> => {
	const cloudId = useCloudId();

	const queryResult = useFetchQuery(
		graphql`
			query getVersions_jira_deployments_versionsForProject_Query($projectId: ID!, $first: Int!) {
				jira @required(action: THROW) {
					versionsForProject(
						jiraProjectId: $projectId
						first: $first
						filter: [UNRELEASED, RELEASED]
					) @required(action: THROW) {
						edges @required(action: THROW) {
							node @required(action: THROW) {
								name @required(action: THROW)
							}
						}
					}
				}
			}
		`,
		{
			projectId: createAri({
				resourceOwner: 'jira',
				cloudId,
				resourceType: 'project',
				resourceId: projectId,
			}),
			first: 500,
		},
		{
			fetchPolicy: 'store-or-network',
		},
	);

	// @ts-expect-error - TS2345 - Argument of type '{ data: unknown; error: Error | null; loading: boolean; }' is not assignable to parameter of type '{ loading: boolean; error: Error | null; data: getVersions_jira_deployments_versionsForProject_Query$data | null; }'.
	return useMemo(() => transformQueryResult(queryResult), [queryResult]);
};
